import * as React from "react";
import {Grid} from "@mui/material";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import MuiAlert from "@mui/material/Alert";
// import {CircularProgress} from "@mui/material";
import AlertTitle from "@mui/material/AlertTitle";
import Papa from "papaparse";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import DatasetUpload from "./DatasetUpload";
import DatasetConfigurator from "./DatasetConfigurator";
import ExperimentTypeSelector from "../experiment/ExperimentTypeSelector";
import {postData, uploadData} from "../../utils/api";
// eslint-disable-next-line import/no-cycle
import {UserContext} from "../../App";
import DatasetAbortWarning from "./DatasetAbortWarning";


const Alert = React.forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function DatasetCreator() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    const steps = [
        t("datasetCreator.UploadDataButton"),
        t("datasetCreator.ConfigureCheckButton"),
        t("datasetCreator.DatasetCreatedButton")
    ];

    const {fetchDatasets} = React.useContext(UserContext);

    const [activeStep, setActiveStep] = React.useState(0);
    const [abortUpload, setAbortUpload] = React.useState(false)
    const [datasetFormData, setDatasetFormData] = React.useState();
    const [showEditor, setShowEditor] = React.useState(false);
    const [columnNamesTooLong, setColumnNamesTooLong] = React.useState([])
    const [newColumnNames, setNewColumnNames] = React.useState([])

    // CSV Data Vars
    const [datasetName, setDatasetName] = React.useState("");
    const [parsedCSV, setParsedCSV] = React.useState(0);
    const [CSVMeta, setCSVMeta] = React.useState(",");

    // Error Alert
    const [alertSettings, setAlertSettings] = React.useState({
        isVisible: false,
        type: "",
        message: "",
    });

    // list with details about errors, when there are more than one error
    const [errorDetailList, setErrorDetailList] = React.useState([]);

    const clearErrors = () => {
        setErrorDetailList([]);
        setShowEditor(false)
        setAlertSettings({
            isVisible: false,
            type: "",
            message: "",
        });
    };

    const handleNext = async () => {
        // on the first step, just go forward
        if (activeStep !== 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
        // on the second step, send gathered information to the backend and wait for response
        else {
            clearErrors()
            try {
                const {status} = await postData(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset`,
                    {datasetName, delimiter: CSVMeta.delimiter}
                );
                if (status !== 201) {
                    // special case for dataset already exists
                    if (status === 409) {
                        setErrorDetailList({DatasetNameAlreadyExists: {}})

                        throw new Error(t("datasetCreator.FailedUploadError"));
                    }
                    throw new Error(t("datasetCreator.CreateDatasetError"));
                }

                if (newColumnNames) {
                    datasetFormData.append("newColumnNames", JSON.stringify(newColumnNames));
                }

                // then post csv data
                await uploadData(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/data`,
                    datasetFormData
                ).then((data) => {
                    if (data.status !== 201) {
                        // check if there is a Problem with the dataset and get details
                        if (data.status === 422) {
                            setErrorDetailList(data.detail)
                            throw new Error(t("datasetCreator.FailedUploadError"));
                        }
                    }
                    // update datasets and set current dataset to just uploaded dataset
                    fetchDatasets({latest: datasetName});
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                });
            } catch (error) {
                // show user error as alert
                setAlertSettings({
                    isVisible: true,
                    type: "error",
                    message: error.toString(),
                });
            }
        }
    };

    const handleColumnNames = (columnNames) => {
        const columnNamesTooLongList = columnNames.filter((name) => name.length >= 24);
        setColumnNamesTooLong(columnNamesTooLongList);

        if (columnNamesTooLongList.length > 0) {
            setShowEditor(true)
            setAlertSettings({
                isVisible: true,
                type: "warning",
                message: t("datasetEditor.ColumnNamesTooLongWarning"),
            });
            setErrorDetailList({ColumnNamesTooLong: {columnNames: columnNamesTooLongList}})
        }
    }

    const handleFileSizeError = (file) => {
        setAlertSettings({
            isVisible: true,
            type: "error",
            message: "",
        });
        setErrorDetailList({FileSizeTooBig: {fileSize: Math.floor(file.size / 1000000)}});
        setActiveStep(0);
    };


    const handleFileUpload = (event) => {

        clearErrors()

        // method to handle csv upload by parsing it
        const file = event.target.files[0];
        if (!file) return;

            // Enforce max size before preview parsing
        if (file.size > 1000000000) {
            handleFileSizeError(file);
            return; // handle Error if file size exceeds the limit
        }

        const SLICE_SIZE = 12 * 1024 * 1024; // 12 MB
        const previewSlice = file.slice(0, SLICE_SIZE);

        // Read the slice as text so we can find the last newline
        const reader = new FileReader();
        reader.onload = ({ target }) => {
            const text = target.result;
            const lastNL = text.lastIndexOf("\n");
            // If we found a newline, only parse up to there
            const safeBlob = lastNL > 0
                ? file.slice(0, lastNL + 1)
                : previewSlice;

            Papa.parse(safeBlob, {
                header: true,
                skipEmptyLines: true,
                complete(results) {
                    setCSVMeta(results.meta);
                    setParsedCSV(results.data);
                    handleColumnNames(results.meta.fields);
                    handleNext();
                },
                error(err) {
                    console.error("🚨 slice parse error:", err);
                    setAlertSettings({
                        isVisible: true,
                        type: "error",
                        message: `Parsing preview failed: ${err.message}`,
                    });
                }
            });
        };
        reader.readAsText(previewSlice);

        // Meanwhile store the full file for the real upload
        const formData = new FormData();
        formData.append("file", file);
        setDatasetFormData(formData);
        setDatasetName(file.name.replace(/\.csv$/, ""));
    };

    const handleBack = () => {
        setAlertSettings((prevAlertSettings) => ({
            ...prevAlertSettings,
            isVisible: false
        }));
        setAbortUpload(!abortUpload)
    };

    const handleAbort = () => {
        //
        if (abortUpload) {
            setErrorDetailList([])
            setAbortUpload(!abortUpload)
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };

    const handleTablePreviewRefresh = (originalRow, updatedRow) => {

        // replace old Row with new Row in CSVMeta
        const originalRowIndex = CSVMeta.fields.indexOf(originalRow);
        if (originalRowIndex !== -1) {
            const updatedFields = [...CSVMeta.fields];
            updatedFields[originalRowIndex] = updatedRow;

            setCSVMeta({
                ...CSVMeta,
                fields: updatedFields,
            });
        }

        // replace old Row with new Row in parsedCSV
        const newArray = parsedCSV.map((obj) => {
            if (obj.hasOwnProperty(originalRow)) {
                let updatedObj = {...obj};
                const entries = Object.entries(updatedObj);
                const indexToRename = entries.findIndex(([key]) => key === originalRow);

                if (indexToRename !== -1) {
                    entries[indexToRename] = [updatedRow, entries[indexToRename][1]];
                    updatedObj = Object.fromEntries(entries);
                }
                return updatedObj;
            }
            return obj;
        });

        setParsedCSV(newArray)

        // update newColumnNames
        const newColumnNamesObj = {}
        newColumnNamesObj[String(originalRow)] = updatedRow;
        setNewColumnNames((prevState) => [...prevState, newColumnNamesObj])
    }

    return (
        <Grid
            id="dataset-creator-container"
            container
            sx={{padding: theme.spacing(8, 2)}}
        >
            <Box  sx={{width: "100%", height: "100%"}}>
                <Stepper activeStep={activeStep}>
                    {steps.map((label) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>

                { /* are there Alerts? */}
                {alertSettings.isVisible &&
                    <Alert severity={alertSettings.type} sx={{marginTop: "20px"}} variant="filled">
                        <AlertTitle>{alertSettings.message}</AlertTitle>
                        {errorDetailList && (
                            <div>
                                {Object.keys(errorDetailList).map(error => (
                                    <Typography variant="h6" color="white"
                                                key={error}> - {t(`datasetError.${error}`, errorDetailList[error])}</Typography>
                                ))}
                            </div>)}
                    </Alert>
                }


                { /* Step Content */}
                {activeStep === 0 && (
                    <DatasetUpload theme={theme} handleFileUpload={handleFileUpload} uploadalertSettings={alertSettings}
                    />
                )}

                {activeStep === 1 && abortUpload &&
                    <DatasetAbortWarning
                        handleBack={handleBack}
                        handleAbort={handleAbort}
                    />}

                { /* Dataset uploads */}
                {activeStep === 1 && !abortUpload &&
                    <>
                        <DatasetConfigurator
                            theme={theme}
                            parsedCSV={parsedCSV}
                            datasetName={datasetName}
                            setDatasetName={setDatasetName}
                            CSVMeta={CSVMeta}
                            errors={errorDetailList}
                            handleBack={handleBack}
                            handleNext={handleNext}
                            activeStep={activeStep}
                            showEditor={showEditor}
                            columnNamesTooLong={columnNamesTooLong}
                            handleTablePreviewRefresh={handleTablePreviewRefresh}
                        />
                    </>
                }


                { /* Dataset successfully uploaded and created */}
                {activeStep === 2 && (
                    <>
                        <br/>
                        <Alert severity="success">
                            <AlertTitle>
                                {t("datasetCreator.DatasetCreated_1")} {datasetName}{" "}
                                {t("datasetCreator.DatasetCreated_2")}
                            </AlertTitle>
                            {t("datasetCreator.DatasetCreated_3")} {datasetName}{" "}
                            {t("datasetCreator.DatasetCreated_4")}
                        </Alert>
                        <ExperimentTypeSelector theme={theme}/>
                    </>
                )}
            </Box>
        </Grid>
    );
}
